/**
 * OpenMeta — i18n system (context maison léger).
 * - Persistance : localStorage `om_lang`
 * - Défaut : locale navigateur (en si commence par 'en') → sinon fr
 * - Accès : useT() → t(key, params?), useLang() → { lang, setLang }
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fr } from './i18n/fr';
import { en } from './i18n/en';

const DICTS = { fr, en };
const STORAGE_KEY = 'om_lang';
const SUPPORTED = ['fr', 'en'];

function detectInitialLang() {
  if (typeof window === 'undefined') return 'fr';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED.includes(stored)) return stored;
  const nav = (window.navigator?.language || 'fr').toLowerCase();
  return nav.startsWith('en') ? 'en' : 'fr';
}

function resolvePath(dict, keyPath) {
  const parts = keyPath.split('.');
  let cur = dict;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

function interpolate(str, params) {
  if (typeof str !== 'string' || !params) return str;
  return str.replace(/\{(\w+)\}/g, (m, k) => (k in params ? String(params[k]) : m));
}

const I18nContext = createContext({
  lang: 'fr',
  setLang: () => {},
  t: (k) => k,
  raw: (k) => undefined,
});

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectInitialLang);

  useEffect(() => {
    // Miroir sur <html lang>
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (_) {}
  }, []);

  const value = useMemo(() => {
    const dict = DICTS[lang] || DICTS.fr;
    const t = (key, params) => {
      const raw = resolvePath(dict, key);
      if (raw == null) return key;
      if (typeof raw === 'string') return interpolate(raw, params);
      return raw;
    };
    const raw = (key) => resolvePath(dict, key);
    return { lang, setLang, t, raw };
  }, [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useT = () => useContext(I18nContext).t;
export const useRaw = () => useContext(I18nContext).raw;
export const useLang = () => {
  const { lang, setLang } = useContext(I18nContext);
  return { lang, setLang };
};

export const LANG_STORAGE_KEY = STORAGE_KEY;
export const SUPPORTED_LANGS = SUPPORTED;
